import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Table from "../../core/common/dataTable/index";
import { API_BASE_URL } from "../../environment";
import { formatCustomDateTime } from "../../utils/dateUtils";
import { showErrorAlert, showSuccessAlert } from "../../utils/alertUtils";
import {
  ContactService,
  Contact as ContactType,
} from "../../feature-module/crm/contacts/contact.service";
import ContactForm from "../../feature-module/crm/contacts/contactForm";
import male from "../../assets/male.png";
import { useTabVisibility } from "../../hooks/useTabVisibility";

interface Contact {
  contact_id: number;
  company_id: number;
  company_name: string;
  contact_full_name: string;
  salutation: string;
  first_name: string;
  last_name: string;
  primary_email: string;
  personal_email: string;
  primary_phone: string;
  mobile_phone: string;
  job_title: string;
  address_line1: string;
  city: string;
  state: string;
  country: string;
  avatar_url: string;
  created_datetime: string;
  updated_datetime: string;
  is_active: boolean;
  assigned_user: {
    user_id: number;
    full_name: string;
    email: string;
    phone: string;
    avatar_url: string;
  } | null;
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  } | null;
  updated_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  } | null;
  tenant_id: number;
}

interface ContactsProps {
  tenant_id?: number | string | null;
  related_entity_id?: number | string | null;
  related_entity_name?: string;
  companyName?: string;
  refreshTrigger?: number;
  onActivityUpdate?: () => void;
}

const Contacts: React.FC<ContactsProps> = ({
  tenant_id,
  related_entity_id,
  related_entity_name,
  companyName,
  refreshTrigger,
  onActivityUpdate,
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResetTrigger, setFormResetTrigger] = useState(0);

  const currentTenantId = tenant_id || localStorage.getItem("selectedTenantId");
  const companyId = related_entity_id;
  const authHeader = localStorage.getItem("authCred");

  const { isVisible, hasBeenActivated } = useTabVisibility({
    tabId: "contacts",
    onTabActivated: () => {
      if (!dataLoaded && currentTenantId && companyId && authHeader) {
        fetchContacts();
        setDataLoaded(true);
      }
    },
  });

  useEffect(() => {
    if (
      hasBeenActivated &&
      !dataLoaded &&
      currentTenantId &&
      companyId &&
      authHeader
    ) {
      fetchContacts();
      setDataLoaded(true);
    }
  }, [hasBeenActivated, dataLoaded, currentTenantId, companyId, authHeader]);

  useEffect(() => {
    if (
      dataLoaded &&
      refreshTrigger &&
      currentTenantId &&
      companyId &&
      authHeader
    ) {
      fetchContacts();
    }
  }, [refreshTrigger, dataLoaded, currentTenantId, companyId, authHeader]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE_URL}/contact/?company_id=${companyId}&tenant_id=${currentTenantId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setContacts(data.results || []);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setError("Failed to load contacts");
      showErrorAlert("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const closeButton = modal.querySelector(
        '[data-bs-dismiss="offcanvas"]'
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(
          modal
        );
        if (bsOffcanvas) {
          bsOffcanvas.hide();
        }
      }
    }
  };

  const handleFormSubmit = async (formData: Partial<ContactType>) => {
    setIsSubmitting(true);
    try {
      let newContact: ContactType;

      if ((formData as any).avatarFile) {
        const formDataWithFile = new FormData();

        Object.keys(formData).forEach((key) => {
          if (key === "avatarFile") {
            formDataWithFile.append("avatar_url", (formData as any)[key]);
          } else if (key !== "avatarFile") {
            const value = (formData as any)[key];
            if (value !== null && value !== undefined) {
              formDataWithFile.append(key, value.toString());
            }
          }
        });

        newContact = await ContactService.createContactWithAvatar(
          formDataWithFile
        );
      } else {
        newContact = await ContactService.createContact(formData);
      }

      await fetchContacts();

      setFormResetTrigger((prev) => prev + 1);
      closeOffcanvas("add_contact_company");

      setTimeout(() => {
        showSuccessAlert(
          `Contact "${newContact.first_name} ${newContact.last_name}" created successfully!`
        );
      }, 500);

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (err) {
      console.error("Failed to create contact:", err);
      showErrorAlert("Failed to create contact");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatLocation = (
    city: string,
    state: string,
    country: string
  ): string => {
    const locationParts = [city, state, country].filter(
      (part) => part && part.trim() !== ""
    );
    return locationParts.length > 0 ? locationParts.join(", ") : "";
  };

  const createContactUrl = (contact: Contact): string => {
    return `/contacts/${contact.contact_id}`;
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "first_name",
      render: (text: string, record: Contact) => (
        <div className="d-flex align-items-center">
          <div className="me-2">
            <div
              className="avatar-sm rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: "32px",
                height: "32px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                overflow: "hidden",
              }}
            >
              <img
                src={record.avatar_url || male}
                alt={`${record.first_name} ${record.last_name}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          </div>
          <Link
            to={createContactUrl(record)}
            target="_blank"
            rel="noopener noreferrer"
            className="d-flex flex-column text-decoration-none"
          >
            <span className="fw-medium">
              {`${record.salutation || ""} ${record.first_name} ${record.last_name
                }`.trim()}
            </span>
            {record.job_title && (
              <span className="text-muted small">{record.job_title}</span>
            )}
          </Link>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "primary_email",
      render: (email: string) => (
        <span className="text-break">{email || "N/A"}</span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "primary_phone",
      render: (phone: string) => <span>{phone || "N/A"}</span>,
    },
    {
      title: "Location",
      dataIndex: "city",
      render: (text: string, record: Contact) => (
        <span>
          {formatLocation(record.city, record.state, record.country) || "N/A"}
        </span>
      ),
    },
    {
      title: "Assigned User",
      dataIndex: "assigned_user",
      render: (assignedUser: Contact["assigned_user"]) => (
        <div className="d-flex align-items-center">
          {assignedUser && (
            <>
              <div className="me-2">
                <div
                  className="avatar-xs rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={assignedUser.avatar_url || male}
                    alt={assignedUser.full_name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              </div>
              <span className="small">{assignedUser.full_name || "N/A"}</span>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="tab-pane fade" id="contacts">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4 className="fw-semibold">
              Contacts
              <span className="count-title ms-1">({contacts.length})</span>
            </h4>
            <div className="d-inline-flex align-items-center">
              <Link
                to="#"
                className="btn btn-primary btn-sm p-2"
                data-bs-toggle="offcanvas"
                data-bs-target="#add_contact_company"
              >
                <i className="fa fa-plus me-1" />
                Add Contact
              </Link>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading contacts...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger text-center">
                <i className="ti ti-alert-circle me-2"></i>
                {error}
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center p-4">
                <div className="mb-3">
                  <i className="ti ti-users fs-1 text-muted"></i>
                </div>
                <h5>No Contacts Found</h5>
                <p className="text-muted">
                  No contacts are associated with this company yet.
                </p>
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#add_contact_company"
                >
                  <i className="fa fa-plus me-1" />
                  Add First Contact
                </Link>
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={contacts}
                  rowKey={(record: Contact) => record.contact_id}
                  className="table table-striped"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Contact Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="add_contact_company"
        style={{ width: "500px" }}
        data-bs-backdrop="static"
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">
            Add Contact for {related_entity_name || "Company"}
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <ContactForm
            onSubmit={handleFormSubmit}
            onCancel={() => { }}
            loading={isSubmitting}
            resetTrigger={formResetTrigger}
            isMainApiCompleted={true}
            isSubmitting={isSubmitting}
            preSelectedCompany={
              companyId && companyName
                ? {
                  company_id: Number(companyId),
                  company_name: companyName,
                }
                : undefined
            }
          />
        </div>
      </div>
    </>
  );
};

export default Contacts;
